import { Link } from 'react-router-dom';
import { MapPin, Briefcase, Clock, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Job, ROUTE_PATHS } from '@/lib/index';
import { motion } from 'framer-motion';

interface JobCardProps {
  job: Job;
}

export function JobCard({ job }: JobCardProps) {
  const formatSalary = (min: number, max: number, currency: string) => {
    const formatNumber = (num: number) => {
      if (num >= 10000) return `${(num / 1000).toFixed(0)}K`;
      return num.toString();
    };
    return `${formatNumber(min)}-${formatNumber(max)} ${currency}`;
  };

  const getJobTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'full-time': '全职',
      'part-time': '兼职',
      'contract': '合同',
      'internship': '实习',
    };
    return labels[type] || type;
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return '今天';
    if (diffInDays === 1) return '昨天';
    if (diffInDays < 7) return `${diffInDays}天前`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}周前`;
    return `${Math.floor(diffInDays / 30)}个月前`;
  };

  return (
    <Link to={ROUTE_PATHS.JOB_DETAIL.replace(':id', job.id)}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{ y: -4 }}
      >
        <Card className="p-6 hover:shadow-lg transition-all duration-200 border border-border hover:border-primary/30 bg-card">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                <img
                  src={job.company.logo}
                  alt={job.company.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    if (target.parentElement) {
                      target.parentElement.innerHTML = `<span class="text-2xl font-bold text-muted-foreground">${job.company.name.charAt(0)}</span>`;
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground mb-1 truncate hover:text-primary transition-colors">
                    {job.title}
                  </h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {job.company.name}
                  </p>
                </div>
                {job.featured && (
                  <Badge variant="default" className="flex-shrink-0 bg-accent text-accent-foreground">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    热门
                  </Badge>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  <span>{job.location}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  <span>{getJobTypeLabel(job.type)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{getTimeAgo(job.postedAt)}</span>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {job.tags.slice(0, 3).map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="text-xs bg-secondary text-secondary-foreground"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {job.tags.length > 3 && (
                    <Badge variant="secondary" className="text-xs bg-secondary text-secondary-foreground">
                      +{job.tags.length - 3}
                    </Badge>
                  )}
                </div>
                <div className="flex-shrink-0 text-right">
                  <p className="text-lg font-bold text-primary font-mono">
                    {formatSalary(job.salary.min, job.salary.max, job.salary.currency)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </Link>
  );
}
